/**
 * Lookup and search over the LHDN unit-of-measurement code list.
 */

import { unitOfMeasureCodes } from "@/constants/unitOfMeasureCodes";

export interface UomOption {
  code: string;
  name: string;
}

/** The published list, normalised to a friendlier shape. */
export const UOM_OPTIONS: UomOption[] = (
  unitOfMeasureCodes as { Code: string; Name: string }[]
).map((entry) => ({ code: entry.Code, name: entry.Name }));

/** Code → option. Codes are unique in the published list. */
const BY_CODE = new Map(UOM_OPTIONS.map((o) => [o.code.toUpperCase(), o]));

/**
 * Lower-cased name → option, for recovering a code from legacy free text.
 *
 */
const BY_NAME = new Map<string, UomOption>();
for (const option of UOM_OPTIONS) {
  const key = option.name.toLowerCase();
  if (!BY_NAME.has(key)) BY_NAME.set(key, option);
}

/** Placeholders the import pipeline uses for "no value". */
const BLANK_TOKENS = new Set(["", "na", "n/a", "null", "undefined", "-"]);

/** True when `code` is a code LHDN will accept. */
export const isValidUomCode = (code: string | null | undefined): boolean =>
  typeof code === "string" && BY_CODE.has(code.trim().toUpperCase());

/** The option for a code, or `undefined` if it is not a published code. */
export const findUom = (
  code: string | null | undefined,
): UomOption | undefined =>
  typeof code === "string" ? BY_CODE.get(code.trim().toUpperCase()) : undefined;

/**
 * The label shown wherever a unit is displayed for selection
 */
export const formatUomLabel = (code: string | null | undefined): string => {
  if (!code) return "";

  const option = findUom(code);
  return option ? `${option.code} - ${option.name}` : code;
};

/** Just the unit's name, for printed documents. Falls back to the raw value. */
export const formatUomName = (code: string | null | undefined): string => {
  if (!code) return "";
  return findUom(code)?.name ?? code;
};

/**
 * Best-effort recovery of a valid code from whatever is stored.
 */
export const resolveUomCode = (
  value: string | null | undefined,
): string | null => {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (BLANK_TOKENS.has(trimmed.toLowerCase())) return null;

  const byCode = BY_CODE.get(trimmed.toUpperCase());
  if (byCode) return byCode.code;

  const byName = BY_NAME.get(trimmed.toLowerCase());
  if (byName) return byName.code;

  return null;
};

/**
 * Ranked search across both code and name
 */
export const searchUom = (query: string, limit = 50): UomOption[] => {
  const q = query.trim().toLowerCase();

  if (!q) return UOM_OPTIONS.slice(0, limit);

  const scored: { option: UomOption; score: number }[] = [];

  for (const option of UOM_OPTIONS) {
    const code = option.code.toLowerCase();
    const name = option.name.toLowerCase();

    let score: number;
    if (code === q) score = 0;
    else if (code.startsWith(q)) score = 1;
    else if (name.startsWith(q)) score = 2;
    else if (name.includes(q)) score = 3;
    else if (code.includes(q)) score = 4;
    else continue;

    scored.push({ option, score });

    if (scored.length >= limit * 4) break;
  }

  return scored
    .sort(
      (a, b) => a.score - b.score || a.option.code.localeCompare(b.option.code),
    )
    .slice(0, limit)
    .map((entry) => entry.option);
};
